import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../services/api";
import logo from "../assets/MaAz_Logo.png";

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      alert("Invalid or missing password reset token");
      return;
    }

    if (!password) {
      alert("Please enter a new password");
      return;
    }

    if (password.length < 8) {
      alert("Password must contain at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const res = await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });

      setMessage(
        res?.data?.message || "Password updated successfully."
      );

      setPassword("");
      setConfirmPassword("");

      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err) {
      console.error("Reset password error:", err);

      const errorMessage =
        err?.response?.data?.message ||
        "Unable to reset password. The link may be invalid or expired.";

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <img src={logo} alt="MaAz Logo" style={styles.logoImg} />

        <h2 style={styles.heading}>Reset Password</h2>

        <p style={styles.description}>
          Enter your new password below.
        </p>

        {!token ? (
          <div style={styles.errorMessage}>
            This password reset link is invalid or incomplete.
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={styles.passwordBox}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.passwordInput}
                autoComplete="new-password"
                required
              />

              <span
                onClick={() =>
                  setShowPassword((previous) => !previous)
                }
                style={styles.eyeIcon}
                role="button"
                tabIndex={0}
                aria-label={
                  showPassword ? "Hide password" : "Show password"
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setShowPassword((previous) => !previous);
                  }
                }}
              >
                {showPassword ? "🙈" : "👁"}
              </span>
            </div>

            <div style={styles.passwordBox}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={styles.passwordInput}
                autoComplete="new-password"
                required
              />

              <span
                onClick={() =>
                  setShowConfirmPassword((previous) => !previous)
                }
                style={styles.eyeIcon}
                role="button"
                tabIndex={0}
                aria-label={
                  showConfirmPassword
                    ? "Hide confirm password"
                    : "Show confirm password"
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setShowConfirmPassword(
                      (previous) => !previous
                    );
                  }
                }}
              >
                {showConfirmPassword ? "🙈" : "👁"}
              </span>
            </div>

            <button
              type="submit"
              style={{
                ...styles.button,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        )}

        {message && (
          <div style={styles.successMessage}>
            {message}
            <div style={{ marginTop: "6px" }}>
              Redirecting to login...
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => navigate("/")}
          style={styles.backButton}
        >
          Back to Login
        </button>
      </div>
    </div>
  );
}

export default ResetPassword;

const styles = {
  container: {
    minHeight: "100vh",
    background: "#f4f6f8",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    boxSizing: "border-box",
  },

  card: {
    width: "400px",
    maxWidth: "100%",
    background: "#fff",
    padding: "35px",
    borderRadius: "14px",
    boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
    textAlign: "center",
    boxSizing: "border-box",
  },

  logoImg: {
    width: "350px",
    maxWidth: "100%",
    marginBottom: "20px",
  },

  heading: {
    marginBottom: "12px",
  },

  description: {
    color: "#666",
    fontSize: "14px",
    lineHeight: "1.6",
    marginBottom: "22px",
  },

  passwordBox: {
    position: "relative",
    marginBottom: "15px",
  },

  passwordInput: {
    width: "100%",
    padding: "12px 42px 12px 12px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
  },

  eyeIcon: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    cursor: "pointer",
    fontSize: "14px",
    color: "#555",
    userSelect: "none",
  },

  button: {
    width: "100%",
    padding: "12px",
    background: "#2c3e50",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontWeight: "600",
  },

  successMessage: {
    marginTop: "18px",
    padding: "12px",
    background: "#eafaf1",
    color: "#1e8449",
    borderRadius: "8px",
    fontSize: "14px",
    lineHeight: "1.5",
  },

  errorMessage: {
    marginBottom: "18px",
    padding: "12px",
    background: "#fdecea",
    color: "#c0392b",
    borderRadius: "8px",
    fontSize: "14px",
    lineHeight: "1.5",
  },

  backButton: {
    marginTop: "18px",
    background: "none",
    border: "none",
    color: "#2c3e50",
    cursor: "pointer",
    textDecoration: "underline",
    fontSize: "14px",
    fontWeight: "600",
  },
};